import React, { useEffect, useState } from 'react';
import { useCloseOnBack } from "../../components/common/useCloseOnBack";
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { FiX, FiCheck } from 'react-icons/fi';
import { FaFortAwesome, FaTicketAlt, FaGolfBall } from 'react-icons/fa';
import { generateTicketCode, formatTicketCode } from './ticketCodeUtils';
import './QRModal.css';

function getTicketIcon(ticket) {
  const name = (ticket?.name || '').toLowerCase();
  if (name.includes('golf')) return { icon: <FaGolfBall size={22} />, color: 'green' };
  if (ticket?.category === 'klippekort') return { icon: <FaTicketAlt size={22} />, color: 'purple' };
  return { icon: <FaFortAwesome size={22} />, color: 'orange' };
}

const QRModal = ({ isOpen, onClose, ticket, tickets, linkedCode }) => {
  const { t } = useTranslation('qrModal');
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    // Lock body scroll and prevent overscroll when modal is open (iOS and Android)
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.touchAction = 'none';
      document.body.style.overscrollBehavior = 'contain';
      document.body.style.webkitOverflowScrolling = 'touch';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.touchAction = '';
        document.body.style.overscrollBehavior = '';
        document.body.style.webkitOverflowScrolling = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      setDragY(0);
      onClose();
    }, 280);
  };
  useCloseOnBack(handleClose);

  const handleDragStart = (clientY) => {
    setIsDragging(true);
    setStartY(clientY);
  };

  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - startY;
    if (deltaY > 0) setDragY(deltaY);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 140) {
      handleClose();
    } else {
      setDragY(0);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    handleDragStart(e.touches[0].clientY);
  };
  const handleTouchMove = (e) => {
    e.preventDefault();
    handleDragMove(e.touches[0].clientY);
  };
  const handleMouseDown = (e) => handleDragStart(e.clientY);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e) => {
      const deltaY = e.clientY - startY;
      if (deltaY > 0) setDragY(deltaY);
    };
    const onMouseUp = () => {
      setIsDragging(false);
      if (dragY > 140) {
        setVisible(false);
        setTimeout(() => {
          setDragY(0);
          onClose();
        }, 280);
      } else {
        setDragY(0);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, startY, dragY, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    
    // Aggressively prevent pull-to-refresh on iOS/Safari
    const preventPullToRefresh = (e) => {
      // Prevent pull-to-refresh when at the top of the page/modal
      const target = e.target;
      const isScrollable = target.scrollHeight > target.clientHeight;
      const isAtTop = target.scrollTop === 0;
      
      if (window.scrollY <= 1 || (isScrollable && isAtTop)) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };

    // Add listener with passive: false to ensure preventDefault works on iOS
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventPullToRefresh);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isLinked = Array.isArray(tickets);

  const formatTicketRef = (ref) => {
    if (!ref || ref === t('unknown')) return t('unknown');
    return `BR-${ref.substring(0, 8)}`;
  };

  const uniqueTicketRef = ticket?.ticketRef || ticket?.firestoreTicketDocId || ticket?.id || t('unknown');
  const displayTicketRef = formatTicketRef(uniqueTicketRef);

  // Generate QR code data - either one ticket, or a bulk-selected subset. The linked
  // group is identified by one shared code (same code as the manual fallback below);
  // the kiosk resolves it server-side to the full list, printing them all from one scan.
  const qrData = isLinked
    // Same trim as the single-ticket case below: parseScan only ever reads `linkCode`
    // out of this - the tickets array was never read by the scanner, just padding
    // based on a since-disproven assumption that a sparser code scanned less reliably.
    ? JSON.stringify({ linkCode: linkedCode })
    // parseScan (rasp-simulator) only ever reads `ticketId` out of this - the backend
    // re-fetches everything else (name, order, duration, etc.) from Firestore once it
    // has that ID. The other fields were pure unused weight, forcing a denser QR than
    // this payload needed.
    : JSON.stringify({ ticketId: ticket.id });

  const { icon, color } = getTicketIcon(isLinked ? null : ticket);

  return ReactDOM.createPortal(
    <div
      className={`qr-modal-overlay ${visible ? 'visible' : 'hidden'}`}
      onClick={handleClose}
      style={{ opacity: visible ? Math.max(0, 1 - dragY / 320) : 0 }}
    >
      <div className={`qr-modal-content ${visible ? 'visible' : 'hidden'}`} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            transform: `translateY(${dragY}px)`,
            transition: isDragging ? 'none' : 'transform 0.28s ease'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleMouseDown}
        >
        <div className="qr-modal-handle" />
        <div className="qr-modal-header">
          <div className="qr-modal-header-left">
            <div className={`qr-modal-icon qr-modal-icon--${color}`}>{icon}</div>
            <div>
              <div className="qr-modal-title">{isLinked ? t('ticketsCount', { count: tickets.length }) : ticket.name}</div>
              <div className="qr-modal-subtitle">{isLinked ? t('sharedQrCode') : t('ticketQrCode')}</div>
            </div>
          </div>
          <button className="qr-modal-close" onClick={handleClose} aria-label={t('close')}>
            <FiX size={16} />
          </button>
        </div>

        <div className="qr-modal-body">
          {!isLinked && (
            <div className="qr-modal-meta">
              <div className="qr-modal-meta-row">
                <span>{t('ticketRef')}</span>
                <strong>{displayTicketRef}</strong>
              </div>
            </div>
          )}

          <div className="qr-modal-qr">
            <QRCodeCanvas
              value={qrData}
              size={350}
              level="H"
              // Forces the same module grid size regardless of payload length, so a
              // single-ticket QR and a shorter bulk linkCode QR render as the same
              // density instead of visibly different "tightness" - version 5 comfortably
              // covers both of these short payloads, so this pads the grid, not the data.
              minVersion={5}
              includeMargin={true}
              // Always full black-on-white contrast - a gray-on-gray "inactive" tint
              // used to be applied here, but that's exactly the QR a customer needs
              // scanned to activate the ticket at the kiosk in the first place, and
              // low-contrast codes are genuinely harder for any scanner (phone or
              // handheld) to lock onto, not just a mobile-camera quirk.
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>

          {isLinked ? (
            <>
              <div className="qr-modal-backup-code">
                <p className="backup-code-label">{t('mCode')}</p>
                <div className="backup-code-value">{formatTicketCode(linkedCode)}</div>
              </div>
              <div className="qr-modal-linked-list">
                <p className="backup-code-label">{t('ticketsInGroup')}</p>
                {tickets.map(tk => (
                  <div className="qr-modal-linked-row" key={tk.id}>
                    <span className="qr-modal-linked-index"><FiCheck size={12} /></span>
                    <span className="qr-modal-linked-name">{tk.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="qr-modal-backup-code">
              <p className="backup-code-label">{t('manualCode')}</p>
              <div className="backup-code-value">{formatTicketCode(generateTicketCode(ticket.id))}</div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QRModal;